import { useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Download, ExternalLink, FileText, Wand2, Layout as LayoutIcon, Lock, Sparkles, Rocket } from "lucide-react";
import {
  generateBuilderPrompt,
  generateDocumentation,
  generateBlueprint,
  DELIVERABLES,
  downloadText,
} from "../lib/deliverables";

const ComingSoonCard = ({ icon: Icon, title, body }) => (
  <div className="rounded-2xl border border-gray-100 bg-[#F7F7F7] p-6 opacity-90" data-testid={`coming-soon-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="w-10 h-10 rounded-xl bg-white text-[#A1A1AA] flex items-center justify-center">
        <Icon size={18} />
      </div>
      <span className="text-[10px] font-bold tracking-wide bg-[#0F0F0F] text-[#D4AF37] px-2.5 py-1 rounded-full">
        COMING SOON
      </span>
    </div>
    <h4 className="font-display font-semibold text-[#0F0F0F]">{title}</h4>
    <p className="text-sm text-[#687280] mt-1 leading-relaxed">{body}</p>
    <button
      disabled
      className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold bg-gray-200 text-gray-400 cursor-not-allowed"
    >
      <Lock size={14} /> Coming Soon
    </button>
  </div>
);

export const DeliveryDashboard = ({ build }) => {
  const [copied, setCopied] = useState(false);

  const prompt = generateBuilderPrompt(build);

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = prompt;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Builder Prompt copied");
    setTimeout(() => setCopied(false), 2500);
  };

  const downloadDocs = () => {
    downloadText(`LaunchBS-${build.app_name.replace(/\s+/g, "-")}-Documentation.txt`, generateDocumentation(build));
    toast.success("Documentation downloaded");
  };

  const downloadBlueprint = () => {
    downloadText(`LaunchBS-${build.app_name.replace(/\s+/g, "-")}-Blueprint.txt`, generateBlueprint(build));
    toast.success("Development Blueprint downloaded");
  };

  return (
    <div data-testid="delivery-dashboard">
      {/* Ready banner */}
      <div className="bg-[#0F0F0F] rounded-3xl p-8 md:p-10 relative overflow-hidden">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] px-4 py-1.5 text-xs font-semibold mb-4">
          <Sparkles size={14} /> DELIVERY COMPLETE
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white tracking-tight">
          Your Launch Preview Is Ready
        </h2>
        <p className="text-[#A1A1AA] mt-3 max-w-2xl leading-relaxed">
          Your app has been professionally designed and prepared for AI-assisted development. You can
          now preview your application and continue building it using your preferred AI builder.
        </p>
      </div>

      {/* Deliverables included */}
      <div className="mt-8">
        <h3 className="font-display text-xl font-bold text-[#0F0F0F] mb-4">Deliverables Included</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
          {DELIVERABLES.map((d) => (
            <div key={d} className="flex items-center gap-2.5 text-sm text-[#0F0F0F]" data-testid={`deliverable-item`}>
              <span className="w-5 h-5 rounded-full bg-[#D4AF37]/15 text-[#B8901F] flex items-center justify-center shrink-0">
                <Check size={12} />
              </span>
              {d}
            </div>
          ))}
        </div>
      </div>

      {/* AI Build Package */}
      <div className="mt-10">
        <h3 className="font-display text-xl font-bold text-[#0F0F0F] mb-4">AI Build Package</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="rounded-2xl border border-[#D4AF37]/40 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
            <div className="w-10 h-10 rounded-xl bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center mb-3">
              <Wand2 size={18} />
            </div>
            <h4 className="font-display font-semibold text-[#0F0F0F]">Builder Prompt</h4>
            <p className="text-sm text-[#687280] mt-1 leading-relaxed">
              A complete AI-ready prompt you can copy directly into your AI builder.
            </p>
            <button
              onClick={copyPrompt}
              className={`mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold transition-colors ${
                copied ? "bg-green-500 text-white" : "bg-[#D4AF37] text-[#0F0F0F] hover:bg-[#c5a030]"
              }`}
              data-testid="copy-builder-prompt-btn"
            >
              {copied ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy Builder Prompt</>}
            </button>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
            <div className="w-10 h-10 rounded-xl bg-[#F7F7F7] text-[#D4AF37] flex items-center justify-center mb-3">
              <FileText size={18} />
            </div>
            <h4 className="font-display font-semibold text-[#0F0F0F]">Project Documentation</h4>
            <p className="text-sm text-[#687280] mt-1 leading-relaxed">
              App overview, features, user flow, database, auth and admin requirements.
            </p>
            <button
              onClick={downloadDocs}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold bg-[#0F0F0F] text-white hover:bg-[#1c1c1c] transition-colors"
              data-testid="download-documentation-btn"
            >
              <Download size={15} /> Download Documentation
            </button>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
            <div className="w-10 h-10 rounded-xl bg-[#F7F7F7] text-[#D4AF37] flex items-center justify-center mb-3">
              <LayoutIcon size={18} />
            </div>
            <h4 className="font-display font-semibold text-[#0F0F0F]">Development Blueprint</h4>
            <p className="text-sm text-[#687280] mt-1 leading-relaxed">
              Folder structure, pages, navigation, components, journey and integrations.
            </p>
            <button
              onClick={downloadBlueprint}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold bg-[#0F0F0F] text-white hover:bg-[#1c1c1c] transition-colors"
              data-testid="download-blueprint-btn"
            >
              <Download size={15} /> Download Blueprint
            </button>
          </div>
        </div>
      </div>

      {/* Live preview */}
      {build.live_preview_url && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-bold text-[#0F0F0F]">Live Preview</h3>
            <button
              onClick={() => window.open(build.live_preview_url, "_blank", "noopener,noreferrer")}
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F0F0F] hover:text-[#D4AF37] transition-colors"
              data-testid="view-live-preview-btn"
            >
              View Live Preview <ExternalLink size={15} />
            </button>
          </div>
          <p className="text-sm text-[#687280] mb-4">Interact with the prototype directly in your browser. No installation required.</p>
          <div className="rounded-2xl border border-gray-100 overflow-hidden shadow-[0_16px_40px_rgb(0,0,0,0.08)] bg-white">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 bg-[#F7F7F7]">
              <span className="w-3 h-3 rounded-full bg-[#FF5F57]" />
              <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
              <span className="w-3 h-3 rounded-full bg-[#28C840]" />
              <span className="ml-3 text-xs text-[#687280] truncate">{build.live_preview_url}</span>
            </div>
            <iframe
              src={build.live_preview_url}
              title={`${build.app_name} live preview`}
              className="w-full h-[600px] bg-white"
              sandbox="allow-scripts allow-forms allow-popups"
              data-testid="build-preview-iframe"
            />
          </div>
        </div>
      )}

      {/* Coming soon packages */}
      <div className="mt-10">
        <h3 className="font-display text-xl font-bold text-[#0F0F0F] mb-4">More Ways to Build</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ComingSoonCard
            icon={Wand2}
            title="AI Builder Package"
            body="A complete production-ready AI development package with enhanced automation and advanced implementation support."
          />
          <ComingSoonCard
            icon={Rocket}
            title="Full Development"
            body="Professional development services for customers who want their application built beyond the Starter Preview."
          />
        </div>
      </div>
    </div>
  );
};
